
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, AlertCircle, InfoIcon, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import { useCssManager } from "@/hooks/useCssManager";

export const CSSOverrideSettings = () => {
  const {
    cssOverride,
    setCssOverride,
    isLoading,
    isSaving,
    error,
    successMessage,
    fetchCssOverride,
    saveCssOverride,
    resetCssOverride
  } = useCssManager();
  
  const { checkSession } = useAuth(true);
  const navigate = useNavigate();

  // Load CSS when component mounts
  useEffect(() => {
    const loadCss = async () => {
      try {
        // First verify authentication
        const session = await checkSession();
        if (!session) {
          navigate("/admin/login");
          return;
        }
        
        await fetchCssOverride();
      } catch (error) {
        if (error.message?.includes("not logged in") || error.message?.includes("session") || error.message?.includes("Admin access required")) {
          navigate("/admin/login");
        }
      }
    };
    
    loadCss();
  }, []);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Custom CSS</CardTitle>
        <CardDescription>
          Add custom CSS to override default styling throughout the application
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {successMessage && (
              <Alert variant="default" className="mb-4 bg-green-50 border-green-200">
                <InfoIcon className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-600">Success</AlertTitle>
                <AlertDescription className="text-green-700">{successMessage}</AlertDescription>
              </Alert>
            )}
            
            <div className="mb-4">
              <Textarea
                placeholder="/* Add your custom CSS here */
.example-class {
  color: blue;
}"
                className="font-mono min-h-[300px]"
                value={cssOverride}
                onChange={(e) => setCssOverride(e.target.value)}
              />
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={saveCssOverride}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : "Save CSS"}
              </Button>
              <Button
                variant="outline"
                onClick={resetCssOverride}
                disabled={isSaving}
              >
                Reset Changes
              </Button>
              <Button
                variant="outline"
                onClick={fetchCssOverride}
                disabled={isSaving}
                className="ml-auto"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
